<script setup>
import { ref, watch } from "vue";
import { useQuizWordsStore } from "@/stores/storeQuizWords";
import { shuffleLetters } from "@/utils/gameWords.js";
import { useModals } from "@/composables/useModals";
import GameHeader from "@/components/quize/quizWords/GameHeader.vue";
import GameBoard from "@/components/quize/quizWords/GameBoard.vue";
import LetterWheel from "@/components/quize/quizWords/LetterWheel.vue";
import CurrentWord from "@/components/quize/quizWords/CurrentWord.vue";

const storeQuizWord = useQuizWordsStore();
const { openModal } = useModals();

const currentWord = ref("");
const toast = ref(null);
const letters = ref([]);
// Генерация букв из dataGame.letters (строка → массив → shuffle)
const generateLetters = () => {
    if (storeQuizWord.dataGame?.letters) {
        letters.value = shuffleLetters(storeQuizWord.dataGame.letters.split(""));
    }
};

// Инициализация
generateLetters();

// При смене данных уровня — новые буквы
watch(
    () => storeQuizWord.dataGame,
    () => {
        generateLetters();
    }
);

function handleWordChanging(word) {
    currentWord.value = word;
}

const ERROR_MESSAGES = {
    562: "Слово не найдено",
    563: "Слово уже было найдено",
    564: "Уровень уже завершён",
};

async function handleWordSelected(word) {
    currentWord.value = "";

    const result = await storeQuizWord.checkAndAddWord(word);
    
    if (!result.success) {
        const message = ERROR_MESSAGES[result.status] || "Ошибка проверки";
        const type = result.status === 563 ? "info" : "error";
        showToast(message, type);
        return;
    }

    showToast("Верно!", "success");

    if (result.data.is_completed) {
        setTimeout(() => {
            storeQuizWord.endGame();
        }, 500);
    }
}

function showToast(message, type) {
    toast.value = { message, type };
    setTimeout(() => {
        toast.value = null;
    }, 1500);
}

// Перемешать буквы заново
function shuffleCurrentLetters() {
    generateLetters();
}

</script>
<template>
    <div class="game">
        <div class="container">
            <div class="game__container">
                <div class="game__header">
                    <h2 class="game__titel game-title">Игра «Листопад»</h2>
                    <GameHeader
                        :level="storeQuizWord.dataGame?.level_number || 1"
                        :total="storeQuizWord.gameProgress?.series?.points_earned || 0"
                    />
                </div>
                <div class="game__inner">
                    <GameBoard
                        v-if="storeQuizWord.dataGame?.grid_data"
                        :gridData="storeQuizWord.dataGame.grid_data"
                    />

                    <div class="game__wheel">
                        <CurrentWord :word="currentWord" />

                        <div class="game__wheel-wrapper">
                            <LetterWheel :letters="letters" @wordSelected="handleWordSelected" @wordChanging="handleWordChanging" />
                            <Transition name="fade">
                                <div v-if="storeQuizWord.isCheckingWord" class="game__checking">
                                    <img width="40" height="40" src="@/assets/img/icons/loader.svg" alt="загрузка" />
                                </div>
                            </Transition>
                        </div>
                    </div>
                </div>

                <button class="btn game__btn --small" @click="openModal('rulesGameWords')">Правила игры</button>
            </div>
        </div>

        <!-- Уведомление  -->
        <Transition name="toast">
            <div v-if="toast" class="toast" :class="toast.type">
                {{ toast.message }}
            </div>
        </Transition>
    </div>
</template>
<style lang="scss" scoped>
@use '@/assets/scss/base/vars';
.game {
    min-height: 100vh;
    background-image: url("@/assets/img/games/shape_words.svg");
    background-repeat: no-repeat;
    background-position: 100% 70%; /* x y*/
    background-size: auto; /* w h*/
    padding: 5.4rem 0 9rem;
    @media (max-width: vars.$l) {
        background-image: none;
    }
    @media (max-width: vars.$x480) {
        padding: 2rem 0 4.6rem;
    }
}

.game__container   {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100rem;
    margin-left: auto;
    margin-right: auto;
    @media (max-width: vars.$t) {
        width: 100%;
    }
}

.game__header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4rem;
    margin-bottom: 4rem;
    @media (max-width: vars.$x480) {
        gap: 2rem;
        width: 100%;
        margin-bottom: 1rem;
    }
}

.game__inner {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    @media (max-width: vars.$mn) {
        flex-direction: column;
        gap: 3rem;
    }
    @media (max-width: vars.$x480) {
        gap: 1.5rem;
    }
}


.game__wheel {

}

.game__wheel-wrapper {
    position: relative;
}

.game__checking {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.4);
    border-radius: 50%;
    z-index: 10;

    img {
        width: 4rem;
        height: 4rem;
        animation: spin 1s linear infinite;
    }
}

@keyframes spin {
    0% { transform: rotate(0); }
    100% { transform: rotate(360deg); }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.game__btn {
    margin-top: 2rem;
    @media (max-width: vars.$x480) {
        height: 4.8rem;
        font-size: 2rem;
        border-radius: 1.6rem;
    }
}

.shuffle-btn {
    background: transparent;
    border: 0.1rem solid rgba(255, 255, 255, 0.3);
    color: rgba(255, 255, 255, 0.7);
    padding: 0.8rem 2rem;
    font-size: 1.4rem;
    border-radius: 0.8rem;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: white;
    }
}

.toast {
    position: fixed;
    top: 8rem;
    left: 50%;
    transform: translateX(-50%);
    padding: 1.2rem 2.4rem;
    border-radius: 1.2rem;
    font-weight: bold;
    color: white;
    z-index: 200;

    &.success {
        // background: linear-gradient(145deg, #4ade80, #22c55e);
        background: var(--fill-accent-2-1);
        color: var(--text-accent-1-100);
    }

    &.error {
        background: linear-gradient(145deg, #f87171, #ef4444);
    }

    &.info {
        // background: linear-gradient(145deg, #60a5fa, #3b82f6);
        background: var(--fill-accent-2-1);
        color: var(--text-accent-1-100);
    }
}

.toast-enter-active,
.toast-leave-active {
    transition: all 0.3s ease;
}

.toast-enter-from {
    opacity: 0;
    transform: translateX(-50%) translateY(-2rem);
}

.toast-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(-2rem);
}
</style>
